import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { itineraryListDefaultParams } from "../config";
import {
  fetchFinalizedBudgetQuoteVersions as fetchFinalizedBudgetQuoteVersionsInService,
  fetchItineraryDispatcherUserCandidates,
  fetchItineraryList,
  fetchItineraryMajorCandidates,
  fetchItineraryPointCandidates,
  fetchItinerarySalesUserCandidates,
  notifyItinerarySales as notifyItinerarySalesInService,
} from "../service";
import { extractItineraryUuid } from "../service/itineraryListSearchService";
import { getProvinceOptions } from "../../../shared/region";
import { isHttpClientError } from "../../../lib/http/errors";
import type {
  ItineraryListAdvancedFilters,
  ItineraryListParams,
  ItineraryListSortBy,
  ItineraryListSortOrder,
  ItineraryScope,
  StaffUserCandidate,
} from "../types";

/** runtime 层导出入口（由脚手架生成）。 */
export { itineraryListDefaultParams };
export * from "./useItineraryEditorRuntime";
export * from "./useItineraryCopyRuntime";
export * from "./useItineraryMoveRuntime";
export * from "./useItineraryInternalInteractions";
export * from "./useItineraryEntryRedirect";
export * from "./itineraryPreviewRuntime";
export * from "./itineraryPdfRuntime";
export * from "./itineraryLocalDraftStore";
export { itineraryRailwayQueries } from "./itineraryRailwayQueryRuntime";
export { itinerarySaveErrorMessage, itinerarySaveErrorDiagnostic } from "../service/itinerarySaveErrorService";
export { countCustomerVisiblePendingTransports } from "../service/itineraryOutputService";
export { derivePublicItinerary } from "../service/itineraryOutputService";
export {
  downloadItineraryPdf,
  downloadPublicItineraryPdf,
} from "../service/itineraryPdfService";

const itineraryListQueryRootKey = ["itinerary", "list"] as const;
const emptyItineraryListAdvancedFilters: ItineraryListAdvancedFilters = {
  majorNames: [],
  durationDays: undefined,
  provinceNames: [],
  stayAreaNames: [],
  pointNames: [],
};

export type ItineraryListCandidateKind = "major" | "province" | "point" | "stay_area";
export type ItineraryListCandidateOption = { label: string; value: string };
const IGNORED_ITINERARY_STAY_NAMES = new Set([
  "火车上",
  "火车上住宿",
  "火车上住宿住宿",
  "无住宿",
  "无住宿住宿",
]);

/** 判断课程线路住宿展示或筛选值是否只是交通过夜、无住宿占位。 */
export function isIgnoredItineraryStayName(value: string): boolean {
  return IGNORED_ITINERARY_STAY_NAMES.has(value.trim());
}

/** 为列表多值筛选提供防抖候选，省份直接复用统一行政区数据。 */
export function useItineraryListCandidateSearch(
  kind: ItineraryListCandidateKind,
  keyword: string,
  selectedValues: string[],
  provinceNames: string[] = [],
  dropdownOpen = false,
) {
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const normalizedKeyword = keyword.trim();
  const candidateProvinceNames = kind === "point" ? provinceNames : [];
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedKeyword(normalizedKeyword), 220);
    return () => window.clearTimeout(timer);
  }, [normalizedKeyword]);

  const remoteQuery = useQuery({
    queryKey: ["itinerary", "list", "candidate", kind,
      kind === "point" || kind === "stay_area" ? normalizedKeyword : debouncedKeyword,
      candidateProvinceNames],
    enabled: kind !== "province" && (kind === "major"
      ? Boolean(debouncedKeyword)
      : dropdownOpen && normalizedKeyword === debouncedKeyword),
    refetchOnWindowFocus: false,
    queryFn: async (context) => {
      if (kind === "major") {
        return {
          options: (await fetchItineraryMajorCandidates(debouncedKeyword)).map((candidate) => ({
            label: candidate.category ? `${candidate.name} · ${candidate.category}` : candidate.name,
            value: candidate.name,
          })),
        };
      }
      if (kind !== "point" && kind !== "stay_area") return { options: [] };
      return {
        options: (await fetchItineraryPointCandidates(
          kind,
          debouncedKeyword,
          candidateProvinceNames,
          context.signal,
        )).items.map((candidate) => ({
          label: candidate.city && !(kind === "stay_area" && candidate.name.trim() === "无")
            ? `${candidate.name} · ${candidate.city}` : candidate.name,
          value: candidate.name,
        })),
      };
    },
    retry: false,
  });

  useEffect(() => {
    if ((kind === "point" || kind === "stay_area") && !dropdownOpen) {
      void queryClient.cancelQueries({
        queryKey: ["itinerary", "list", "candidate", kind, normalizedKeyword, candidateProvinceNames],
        exact: true,
      });
    }
  }, [candidateProvinceNames, dropdownOpen, kind, normalizedKeyword, queryClient]);

  const options = useMemo(() => {
    const currentOptions = selectedValues.map((value) => ({ label: value, value }));
    const candidateOptions = kind === "province"
      ? getProvinceOptions()
        .filter((value) => !debouncedKeyword || value.includes(debouncedKeyword))
        .map((value) => ({ label: value, value }))
      : remoteQuery.data?.options ?? [];
    const seen = new Set<string>();
    return [...currentOptions, ...candidateOptions].filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [debouncedKeyword, kind, remoteQuery.data?.options, selectedValues]);

  return { options, loading: remoteQuery.isFetching };
}

/** 为销售/计调筛选下拉提供初始人员候选。 */
export function useItineraryStaffUserCandidates(role: "sales" | "dispatcher") {
  const query = useQuery({
    queryKey: ["itinerary", "list", "staff-candidates", role],
    queryFn: () => (
      role === "sales"
        ? fetchItinerarySalesUserCandidates()
        : fetchItineraryDispatcherUserCandidates()
    ),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    options: query.data ?? ([] as StaffUserCandidate[]),
    loading: query.isLoading || query.isFetching,
  };
}

/** 在列表与下游业务页之间往返时保留的列表视图状态。 */
export type ItineraryListNavigationState = {
  scope: ItineraryScope;
  draftItineraryIdInput: string;
  submittedItineraryId: string;
  draftKeyword: string;
  submittedKeyword: string;
  draftSchoolName?: string;
  submittedSchoolName?: string;
  draftSalesUserId?: string;
  submittedSalesUserId?: string;
  draftSalesUserName?: string;
  submittedSalesUserName?: string;
  draftDispatcherUserId?: string;
  submittedDispatcherUserId?: string;
  draftDispatcherUserName?: string;
  submittedDispatcherUserName?: string;
  draftAdvancedFilters?: ItineraryListAdvancedFilters;
  submittedAdvancedFilters?: ItineraryListAdvancedFilters;
  sortBy?: ItineraryListSortBy;
  sortOrder?: ItineraryListSortOrder;
  page: number;
  pageSize: number;
};

/** 构建行程核价入口路径。 */
export function buildItineraryBudgetPath(itineraryId: string): string {
  return `/quote/itineraries/${encodeURIComponent(itineraryId)}/budget`;
}

/** 构建指定核价版本编辑页路径。 */
export function buildQuoteBudgetPath(budgetOrderId: string): string {
  return `/quote/budgets/${encodeURIComponent(budgetOrderId)}`;
}

/** 构建最终报价精确详情路径。 */
export function buildCustomerQuotePath(customerQuoteId: string): string {
  return `/quote/customer-quotes/${encodeURIComponent(customerQuoteId)}`;
}

/** 构建从定稿核价进入对应对客报价的中转路径。 */
export function buildCustomerQuoteEntryPath(budgetOrderId: string): string {
  return `/quote/budgets/${encodeURIComponent(budgetOrderId)}/customer-quote`;
}

/** 构建课程线路确认件入口路径。 */
export function buildItineraryFulfillmentPath(itineraryId: string): string {
  return `/supply-chain/fulfillment-orders/itineraries/${encodeURIComponent(itineraryId)}/enter`;
}

/** 构建行程编辑入口路径。 */
export function buildItineraryEditPath(itineraryId: string): string {
  return `/itineraries/${encodeURIComponent(itineraryId)}/edit`;
}

/** 构建行程独立预览入口路径。 */
export function buildItineraryPreviewPath(itineraryId: string): string {
  return `/itineraries/${encodeURIComponent(itineraryId)}/preview`;
}

/** 课程线路列表允许写入 URL 的范围取值。 */
export const ITINERARY_LIST_SCOPES: readonly ItineraryScope[] = [
  "mine",
  "recent_3y",
  "all",
  "mine_formed",
];

/** 解析课程线路列表 URL 中的 scope；非法值视为未指定。 */
export function parseItineraryListScope(value: string | null | undefined): ItineraryScope | undefined {
  if (!value) return undefined;
  return ITINERARY_LIST_SCOPES.includes(value as ItineraryScope)
    ? (value as ItineraryScope)
    : undefined;
}

/** 构建线路列表中的统一行程预览抽屉入口。 */
export function buildItineraryListPreviewPath(itineraryId: string): string {
  return `/product-lines/course-routes?id=${encodeURIComponent(itineraryId)}`;
}

/** 向课程线路所属项目的销售人员发送微信通知。 */
export function notifyItinerarySales(projectId: string, itineraryId: string) {
  return notifyItinerarySalesInService(projectId, itineraryId);
}

/** 查询当前行程全部已定稿核价及其对应报价。 */
export function fetchFinalizedBudgetQuoteVersions(itineraryId: string) {
  return fetchFinalizedBudgetQuoteVersionsInService(itineraryId);
}

/** 管理课程线路列表筛选、分页与服务端查询。 */
export function useItineraryListRuntime(initialState?: ItineraryListNavigationState) {
  const [scope, setScope] = useState<ItineraryScope>(initialState?.scope ?? itineraryListDefaultParams.scope);
  const [draftItineraryIdInput, setDraftItineraryIdInputState] = useState(initialState?.draftItineraryIdInput ?? "");
  const [submittedItineraryId, setSubmittedItineraryId] = useState(initialState?.submittedItineraryId ?? "");
  const [itineraryIdError, setItineraryIdError] = useState("");
  const [draftKeyword, setDraftKeywordState] = useState(initialState?.draftKeyword ?? "");
  const [submittedKeyword, setSubmittedKeyword] = useState(initialState?.submittedKeyword ?? "");
  const [draftSchoolName, setDraftSchoolNameState] = useState(initialState?.draftSchoolName ?? "");
  const [submittedSchoolName, setSubmittedSchoolName] = useState(initialState?.submittedSchoolName ?? "");
  const [draftSalesUserId, setDraftSalesUserIdState] = useState(initialState?.draftSalesUserId ?? "");
  const [submittedSalesUserId, setSubmittedSalesUserId] = useState(
    initialState?.submittedSalesUserId ?? "",
  );
  const [draftSalesUserName, setDraftSalesUserNameState] = useState(
    initialState?.draftSalesUserName ?? "",
  );
  const [submittedSalesUserName, setSubmittedSalesUserName] = useState(
    initialState?.submittedSalesUserName ?? "",
  );
  const [draftDispatcherUserId, setDraftDispatcherUserIdState] = useState(
    initialState?.draftDispatcherUserId ?? "",
  );
  const [submittedDispatcherUserId, setSubmittedDispatcherUserId] = useState(
    initialState?.submittedDispatcherUserId ?? "",
  );
  const [draftDispatcherUserName, setDraftDispatcherUserNameState] = useState(
    initialState?.draftDispatcherUserName ?? "",
  );
  const [submittedDispatcherUserName, setSubmittedDispatcherUserName] = useState(
    initialState?.submittedDispatcherUserName ?? "",
  );
  const [draftAdvancedFilters, setDraftAdvancedFiltersState] = useState<ItineraryListAdvancedFilters>(
    () => normalizeAdvancedFilters(initialState?.draftAdvancedFilters ?? emptyItineraryListAdvancedFilters),
  );
  const [submittedAdvancedFilters, setSubmittedAdvancedFilters] = useState<ItineraryListAdvancedFilters>(
    () => normalizeAdvancedFilters(initialState?.submittedAdvancedFilters ?? emptyItineraryListAdvancedFilters),
  );
  const [sortBy, setSortBy] = useState<ItineraryListSortBy>(
    initialState?.sortBy ?? itineraryListDefaultParams.sortBy,
  );
  const [sortOrder, setSortOrder] = useState<ItineraryListSortOrder>(
    initialState?.sortOrder ?? itineraryListDefaultParams.sortOrder,
  );
  const [page, setPage] = useState(initialState?.page ?? itineraryListDefaultParams.page);
  const [pageSize, setPageSize] = useState(initialState?.pageSize ?? itineraryListDefaultParams.pageSize);
  const params = useMemo<ItineraryListParams>(
    () => ({
      scope,
      itineraryId: submittedItineraryId || undefined,
      keyword: submittedKeyword || undefined,
      schoolName: submittedSchoolName || undefined,
      salesUserId: submittedSalesUserId || undefined,
      dispatcherUserId: submittedDispatcherUserId || undefined,
      majorNames: submittedAdvancedFilters.majorNames.length > 0
        ? submittedAdvancedFilters.majorNames
        : undefined,
      durationDays: submittedAdvancedFilters.durationDays,
      provinceNames: submittedAdvancedFilters.provinceNames.length > 0
        ? submittedAdvancedFilters.provinceNames
        : undefined,
      stayAreaNames: submittedAdvancedFilters.stayAreaNames.length > 0
        ? submittedAdvancedFilters.stayAreaNames
        : undefined,
      pointNames: submittedAdvancedFilters.pointNames.length > 0
        ? submittedAdvancedFilters.pointNames
        : undefined,
      sortBy,
      sortOrder,
      page,
      pageSize,
    }),
    [
      page,
      pageSize,
      scope,
      sortBy,
      sortOrder,
      submittedAdvancedFilters,
      submittedDispatcherUserId,
      submittedItineraryId,
      submittedKeyword,
      submittedSalesUserId,
      submittedSchoolName,
    ],
  );
  const query = useQuery({
    queryKey: [...itineraryListQueryRootKey, params],
    queryFn: () => fetchItineraryList(params),
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const listPage = query.data ?? { items: [], total: 0, page, pageSize };
  const navigationState = useMemo<ItineraryListNavigationState>(() => ({
    scope,
    draftItineraryIdInput,
    submittedItineraryId,
    draftKeyword,
    submittedKeyword,
    draftSchoolName,
    submittedSchoolName,
    draftSalesUserId,
    submittedSalesUserId,
    draftSalesUserName,
    submittedSalesUserName,
    draftDispatcherUserId,
    submittedDispatcherUserId,
    draftDispatcherUserName,
    submittedDispatcherUserName,
    draftAdvancedFilters,
    submittedAdvancedFilters,
    sortBy,
    sortOrder,
    page,
    pageSize,
  }), [
    draftAdvancedFilters,
    draftDispatcherUserId,
    draftDispatcherUserName,
    draftItineraryIdInput,
    draftSalesUserId,
    draftSalesUserName,
    draftKeyword,
    draftSchoolName,
    page,
    pageSize,
    scope,
    sortBy,
    sortOrder,
    submittedAdvancedFilters,
    submittedDispatcherUserId,
    submittedDispatcherUserName,
    submittedItineraryId,
    submittedKeyword,
    submittedSalesUserId,
    submittedSalesUserName,
    submittedSchoolName,
  ]);

  /** 校验并提交当前界面可见的列表筛选条件。 */
  function submitDraftFilters(nextScope: ItineraryScope) {
    const normalizedIdInput = draftItineraryIdInput.trim();
    const itineraryId = normalizedIdInput
      ? extractItineraryUuid(normalizedIdInput)
      : undefined;
    if (normalizedIdInput && !itineraryId) {
      setItineraryIdError("请输入有效的行程 UUID 或包含 UUID 的链接");
      return;
    }
    const normalizedKeyword = draftKeyword.trim();
    const normalizedSchoolName = Array.from(draftSchoolName.trim()).slice(0, 100).join("");
    const normalizedSalesUserId = draftSalesUserId.trim();
    const normalizedSalesUserName = draftSalesUserName.trim();
    const normalizedDispatcherUserId = draftDispatcherUserId.trim();
    const normalizedDispatcherUserName = draftDispatcherUserName.trim();
    const normalizedAdvancedFilters = normalizeAdvancedFilters(draftAdvancedFilters);
    const canRefreshCurrentQuery = page === 1
      && nextScope === scope
      && (itineraryId ?? "") === submittedItineraryId
      && normalizedKeyword === submittedKeyword
      && normalizedSchoolName === submittedSchoolName
      && normalizedSalesUserId === submittedSalesUserId
      && normalizedDispatcherUserId === submittedDispatcherUserId
      && areAdvancedFiltersEqual(normalizedAdvancedFilters, submittedAdvancedFilters);
    setItineraryIdError("");
    setScope(nextScope);
    setSubmittedItineraryId(itineraryId ?? "");
    setSubmittedKeyword(normalizedKeyword);
    setSubmittedSchoolName(normalizedSchoolName);
    setSubmittedSalesUserId(normalizedSalesUserId);
    setSubmittedSalesUserName(normalizedSalesUserName);
    setSubmittedDispatcherUserId(normalizedDispatcherUserId);
    setSubmittedDispatcherUserName(normalizedDispatcherUserName);
    setSubmittedAdvancedFilters(normalizedAdvancedFilters);
    setPage(1);
    if (canRefreshCurrentQuery) void query.refetch();
  }

  return {
    scope,
    draftItineraryIdInput,
    itineraryIdError,
    draftKeyword,
    draftSchoolName,
    draftSalesUserId,
    draftSalesUserName,
    draftDispatcherUserId,
    draftDispatcherUserName,
    draftAdvancedFilters,
    sortBy,
    sortOrder,
    items: listPage.items,
    total: listPage.total,
    page,
    pageSize,
    navigationState,
    loading: query.isLoading || query.isFetching,
    errorMessage: resolveItineraryListErrorMessage(query.error),
    setDraftItineraryIdInput(value: string) {
      setDraftItineraryIdInputState(value);
      setItineraryIdError("");
    },
    setDraftKeyword(value: string) {
      setDraftKeywordState(Array.from(value).slice(0, 100).join(""));
    },
    setDraftSchoolName(value: string) {
      setDraftSchoolNameState(Array.from(value).slice(0, 100).join(""));
    },
    setDraftSalesUser(userId?: string, displayName?: string) {
      setDraftSalesUserIdState(userId?.trim() ?? "");
      setDraftSalesUserNameState(displayName?.trim() ?? "");
    },
    setDraftDispatcherUser(userId?: string, displayName?: string) {
      setDraftDispatcherUserIdState(userId?.trim() ?? "");
      setDraftDispatcherUserNameState(displayName?.trim() ?? "");
    },
    setDraftAdvancedFilters(value: Partial<ItineraryListAdvancedFilters>) {
      setDraftAdvancedFiltersState((current) => normalizeAdvancedFilters({ ...current, ...value }));
    },
    handleScopeChange(nextScope: ItineraryScope) {
      submitDraftFilters(nextScope);
    },
    handleSearch() {
      submitDraftFilters(scope);
    },
    handleReset() {
      setDraftItineraryIdInputState("");
      setSubmittedItineraryId("");
      setItineraryIdError("");
      setDraftKeywordState("");
      setSubmittedKeyword("");
      setDraftSchoolNameState("");
      setSubmittedSchoolName("");
      setDraftSalesUserIdState("");
      setSubmittedSalesUserId("");
      setDraftSalesUserNameState("");
      setSubmittedSalesUserName("");
      setDraftDispatcherUserIdState("");
      setSubmittedDispatcherUserId("");
      setDraftDispatcherUserNameState("");
      setSubmittedDispatcherUserName("");
      setDraftAdvancedFiltersState(emptyItineraryListAdvancedFilters);
      setSubmittedAdvancedFilters(emptyItineraryListAdvancedFilters);
      setSortBy(itineraryListDefaultParams.sortBy);
      setSortOrder(itineraryListDefaultParams.sortOrder);
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    handleSort(nextSortBy: ItineraryListSortBy, nextSortOrder: ItineraryListSortOrder) {
      setSortBy(nextSortBy);
      setSortOrder(nextSortOrder);
      setPage(1);
    },
    retry() {
      void query.refetch();
    },
  };
}

/** 规整列表多值筛选，避免空白值和重复值进入 query key。 */
function normalizeAdvancedFilters(
  filters: ItineraryListAdvancedFilters,
): ItineraryListAdvancedFilters {
  const normalizeValues = (values: string[] | undefined) => Array.from(new Set(
    (values ?? []).map((item) => item.trim()).filter(Boolean),
  ));
  return {
    majorNames: normalizeValues(filters.majorNames),
    durationDays: filters.durationDays,
    provinceNames: normalizeValues(filters.provinceNames),
    stayAreaNames: normalizeValues(filters.stayAreaNames).filter((value) => (
      !isIgnoredItineraryStayName(value)
    )),
    pointNames: normalizeValues(filters.pointNames),
  };
}

/** 比较已规范化的高级筛选，避免相同条件提交时被 query key 去重。 */
function areAdvancedFiltersEqual(
  left: ItineraryListAdvancedFilters,
  right: ItineraryListAdvancedFilters,
): boolean {
  const areValuesEqual = (leftValues: string[], rightValues: string[]) => (
    leftValues.length === rightValues.length
    && leftValues.every((value, index) => value === rightValues[index])
  );
  return left.durationDays === right.durationDays
    && areValuesEqual(left.majorNames, right.majorNames)
    && areValuesEqual(left.provinceNames, right.provinceNames)
    && areValuesEqual(left.stayAreaNames, right.stayAreaNames)
    && areValuesEqual(left.pointNames, right.pointNames);
}

/** 把课程线路列表超时收敛成可恢复的稳定提示。 */
function resolveItineraryListErrorMessage(error: unknown): string {
  if (!error) return "";
  if (
    isHttpClientError(error)
    && (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT")
  ) {
    return "课程线路查询超时，请重试";
  }
  return "课程线路列表加载失败，请稍后重试";
}
